import { OPENAI_MODEL, SYSTEM_PROMPT } from './constants';
import { dbgLog } from './debug';

export interface SceneResponse {
  title: string;
  layers: Array<{ label: string; sfx_prompt: string }>;
}

export async function callOpenAI(sceneText: string, openaiKey: string): Promise<SceneResponse> {
  const model           = localStorage.getItem('openai_model')           || OPENAI_MODEL;
  const tokens          = parseInt(localStorage.getItem('openai_tokens') || '16000', 10);
  const reasoning       = localStorage.getItem('openai_reasoning')       || null;
  const reasoningTokens = localStorage.getItem('openai_reasoning_tokens') ? parseInt(localStorage.getItem('openai_reasoning_tokens')!, 10) : null;
  dbgLog('info', `OpenAI → POST chat/completions (model: ${model}, max_completion_tokens: ${tokens}${reasoning ? `, reasoning_effort: ${reasoning}` : ''}${reasoningTokens ? `, max_reasoning_tokens: ${reasoningTokens}` : ''})`);

  const body: Record<string, unknown> = {
    model,
    max_completion_tokens: tokens,
    messages: [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user',   content: sceneText },
    ],
  };
  if (reasoning) body.reasoning_effort = reasoning;
  if (reasoningTokens) body.max_reasoning_tokens = reasoningTokens;

  const resp = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${openaiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  dbgLog('info', `OpenAI ← HTTP ${resp.status} ${resp.statusText}`);

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: { message?: string } };
    dbgLog('error', 'OpenAI error body', err);
    throw new Error(`OpenAI error ${resp.status}: ${err?.error?.message || resp.statusText}`);
  }

  const data = await resp.json() as {
    choices?: Array<{ message?: { content?: string } }>;
    usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number; completion_tokens_details?: { reasoning_tokens?: number } };
  };

  if (data.usage) {
    const u = data.usage;
    const reasoning = u.completion_tokens_details?.reasoning_tokens ?? 0;
    dbgLog('info', `OpenAI tokens — prompt: ${u.prompt_tokens}, completion: ${u.completion_tokens}${reasoning ? ` (${reasoning} reasoning)` : ''}, total: ${u.total_tokens}`);
  }

  const raw = data.choices?.[0]?.message?.content ?? '';
  dbgLog('info', 'OpenAI content string', raw);

  if (!raw) throw new Error('OpenAI returned empty content.');

  const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  dbgLog('info', 'Cleaned JSON string', cleaned);

  const parsed = JSON.parse(cleaned) as SceneResponse;
  dbgLog('ok', 'JSON parsed successfully', parsed);
  return parsed;
}

export async function callElevenLabs(sfx_prompt: string, elevenKey: string): Promise<ArrayBuffer> {
  dbgLog('info', `ElevenLabs → sound-generation`, { text: sfx_prompt.slice(0, 80) + '…' });
  const resp = await fetch('https://api.elevenlabs.io/v1/sound-generation', {
    method: 'POST',
    headers: {
      'xi-api-key': elevenKey,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      text: sfx_prompt,
      duration_seconds: 30,
      prompt_influence: 0.7,
    }),
  });

  dbgLog('info', `ElevenLabs ← HTTP ${resp.status} ${resp.statusText}, content-type: ${resp.headers.get('content-type')}`);

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { detail?: { message?: string } | string };
    dbgLog('error', 'ElevenLabs error body', err);
    const msg = typeof err.detail === 'object' ? err.detail?.message : err.detail;
    throw new Error(`ElevenLabs error ${resp.status}: ${msg || resp.statusText}`);
  }

  const buf = await resp.arrayBuffer();
  dbgLog('ok', `ElevenLabs audio received — ${(buf.byteLength / 1024).toFixed(1)} KB`);
  return buf;
}
